/**
 * Focused ABI for a TermMax curated vault (`TermMaxVaultV2`) plus the ERC-20
 * reads used to resolve the underlying's decimals.
 *
 * Deliberately tolerant: `apr()` and `accretingPrincipal()` are declared but
 * REVERT on the deployed 2.0.0 vaults (they exist in the newer interface only),
 * so every call goes through `allowFailure: true` and the parser falls back.
 */
export const TermMaxVaultAbi = [
  { type: 'function', name: 'name', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'symbol', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  { type: 'function', name: 'asset', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'totalAssets', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'totalSupply', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  {
    type: 'function',
    name: 'convertToAssets',
    stateMutability: 'view',
    inputs: [{ name: 'shares', type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  // GROSS annual interest in underlying units. The performance fee is deducted
  // from it on accrual, so it must be netted before publishing a rate.
  { type: 'function', name: 'annualizedInterest', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  // Depositors' accruing claim — the correct APR denominator. REVERTS on 2.0.0.
  { type: 'function', name: 'accretingPrincipal', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  // 1e8-scaled (0.1e8 = 10%).
  { type: 'function', name: 'performanceFeeRate', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint64' }] },
  // Pre-computed net APY. REVERTS on 2.0.0 — we derive instead.
  { type: 'function', name: 'apr', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'curator', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'guardian', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'timelock', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'getVersion', stateMutability: 'pure', inputs: [], outputs: [{ type: 'string' }] },
] as const

export const Erc20DecimalsBalanceAbi = [
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const
